import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { ButtonGroup, ButtonGroupItem } from "@/components/flexkit/ButtonGroup";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { cn } from "@/components/podkit/lib/cn";
import { toast } from "@/components/podkit/toasts/use-toast";
import { DeleteProjectModal } from "@/components/projects/ProjectDeleteModal";
import { ProjectPhaseTag } from "@/components/projects/ProjectPhaseTag";
import { SourceControlProviderIcon } from "@/components/runners/details/SourceControlProviderIcon";
import { Flags } from "@/feature-flags";
import { formatRepoUrlForDisplay } from "@/format/url";
import { useFlag } from "@/hooks/use-flag";
import { useIsPointerCoarse } from "@/hooks/use-is-pointer-coarse";
import { useShareResource } from "@/hooks/use-share-resource";
import { getProjectInitializer, getRepoUrlFromInitializer } from "@/queries/environment-queries";
import type { PlainGroup, PlainRoleAssignment } from "@/queries/group-queries";
import { useOrganization, type PlainOrganizationMember } from "@/queries/organization-queries";
import { useCreatePrebuild, useProjectPrebuilds } from "@/queries/prebuild-queries";
import type { PlainProject, ProjectState } from "@/queries/project-queries";
import { CreateEnvironmentButton } from "@/routes/environments/create/CreateEnvironmentButton";
import { formatError } from "@/utils/errors";
import { getProjectUsers } from "@/utils/projects";
import { GitInitializer_CloneTargetMode } from "gitpod-next-api/gitpod/v1/environment_pb";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { ProjectPhase } from "gitpod-next-api/gitpod/v1/project_pb";
import { GitBranchIcon } from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";
import { Link, useNavigate } from "react-router-dom";
import { groupToStackItem, userToStackItem } from "./avatar-stack-helpers";
import { AvatarStack, type AvatarStackItem } from "./AvatarStack";
import { AvatarStackPopoverUserList } from "./AvatarStackPopoverUserList";

type Props = {
    canEdit: boolean;
    project: PlainProject;
    state?: ProjectState;
    orgMembers?: PlainOrganizationMember[];
    currentUserId: string;
    onCreateSuccess?: () => void;
    forceHoverState?: boolean;
    scmId?: string;
    roleAssignments?: PlainRoleAssignment[];
    allGroups?: PlainGroup[];
};
export const ProjectCard: FC<Props> = ({
    canEdit,
    project,
    state,
    orgMembers,
    currentUserId,
    onCreateSuccess,
    forceHoverState,
    scmId,
    roleAssignments,
    allGroups,
}) => {
    const isPointerCoarse = useIsPointerCoarse();
    const { value: isGroupManagementEnabled } = useFlag(Flags.GroupManagementEnabled, false);
    const { value: isPaginatedProjectsEnabled } = useFlag(Flags.PaginatedProjectsList, false);

    const { data: organization } = useOrganization();

    const shouldShowOptionsDropdown = useMemo(() => {
        return !!project?.id && state?.environmentClasses && state.environmentClasses.length > 1;
    }, [project, state]);

    const projectContextUrl = getRepoUrlFromInitializer(project.initializer);
    const initializer = getProjectInitializer(project.initializer);
    const users = getProjectUsers(project, orgMembers);
    const creator = orgMembers?.find((m) => m.userId === project.metadata?.creator?.id);
    const currentMember = orgMembers?.find((m) => m.userId === currentUserId);
    const isAdmin = currentMember?.role === OrganizationRole.ADMIN;
    const isCreator = project.metadata?.creator?.id === (currentUserId || "no-member");
    const editable = canEdit && (isAdmin || isCreator);

    const [menuOpen, setMenuOpen] = useState(false);

    const isPendingDeletion = project.desiredPhase === ProjectPhase.DELETED;
    const prebuildsQuery = useProjectPrebuilds(isPendingDeletion ? project.id : undefined);
    let prebuildCount = 0;
    if (prebuildsQuery.isSuccess && prebuildsQuery.data) {
        prebuildCount = prebuildsQuery.data.prebuilds?.length ?? 0;
    }

    // Get groups that have access to this project using passed-in data
    const sharedWithGroups = useMemo(() => {
        if (!isGroupManagementEnabled || !roleAssignments || !allGroups) {
            return [];
        }

        const groupIds = new Set(roleAssignments.map((a) => a.groupId));
        return allGroups.filter((g) => groupIds.has(g.id) && !g.systemManaged);
    }, [isGroupManagementEnabled, roleAssignments, allGroups]);

    // Avatar stack items for paginated version (users only)
    const avatarStackItems = useMemo(() => {
        return users?.map(userToStackItem);
    }, [users]);

    // Group stack items for paginated version (groups + org indicator, shown in header)
    const groupStackItems = useMemo(() => {
        const stateIcon: AvatarStackItem | undefined = state?.shared
            ? {
                  id: "state",
                  name: organization?.name ?? "Everyone",
                  shape: "square",
                  tooltip: "Everyone at " + organization?.name,
              }
            : undefined;

        return [...(sharedWithGroups?.map(groupToStackItem) ?? []), ...(stateIcon ? [stateIcon] : [])];
    }, [organization?.name, sharedWithGroups, state?.shared]);

    // Combined items for non-paginated version (groups + users together)
    const combinedAvatarStackItems = useMemo(() => {
        return [...sharedWithGroups.map(groupToStackItem), ...(users?.map(userToStackItem) ?? [])];
    }, [sharedWithGroups, users]);

    return (
        <Link
            data-testid={"project-" + project.id}
            className={cn(
                "@container", // We use a container to allow us to responsively shrink or expand the avatar stack when collapsed.
                "group flex h-full min-h-[180px] flex-col gap-3 rounded-lg border border-solid border-border-light bg-surface-glass p-5 text-left transition-all duration-200",
                isPaginatedProjectsEnabled && "justify-between",
                "hover:border-border-medium hover:shadow-md",
                forceHoverState && "border-border-medium shadow-md",
            )}
            to={`/projects/${project.id}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="mb-2 flex items-center gap-2">
                        <h3
                            className={cn(
                                "text-xl font-semibold leading-tight text-content-primary",
                                isPaginatedProjectsEnabled && "flex w-full items-baseline justify-between gap-2",
                            )}
                        >
                            {project.metadata?.name}
                            {isPaginatedProjectsEnabled && (
                                <AvatarStack items={groupStackItems} disablePopover className="hidden @xs:flex" />
                            )}
                        </h3>
                        <ProjectPhaseTag project={project} prebuildCount={prebuildCount} />
                    </div>
                    <div
                        className="grid grid-cols-[auto,1fr] items-center gap-x-2 gap-y-1 text-sm text-content-secondary"
                        title={projectContextUrl}
                    >
                        <SourceControlProviderIcon scmId={scmId} size="base" className="flex-shrink-0" />
                        <span className="truncate font-mono text-xs">
                            {formatRepoUrlForDisplay(projectContextUrl, true)}
                        </span>
                        {initializer?.targetMode === GitInitializer_CloneTargetMode.REMOTE_BRANCH && (
                            <>
                                <div className="flex size-4 items-center justify-center">
                                    <GitBranchIcon className="size-3 flex-shrink-0" />
                                </div>
                                <span className="font-mono text-xs">{initializer.cloneTarget}</span>
                            </>
                        )}
                    </div>
                </div>
                <div
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    data-tracking-id-none
                >
                    <ProjectActions
                        project={project}
                        editable={editable}
                        currentMember={currentMember}
                        state={state}
                        creator={creator}
                    />
                </div>
            </div>
            <div
                className={cn(
                    "relative flex justify-between",
                    isPaginatedProjectsEnabled
                        ? "flex-col items-start gap-2 @xs:flex-row @xs:items-center"
                        : "min-h-[2.5rem] items-center",
                )}
            >
                <div
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    data-tracking-id-none
                >
                    <AvatarStack
                        items={isPaginatedProjectsEnabled ? avatarStackItems : combinedAvatarStackItems}
                        maxVisible={5}
                        morePopoverContent={<AvatarStackPopoverUserList project={project} />}
                    />
                </div>
                {!isPendingDeletion && (
                    <ButtonGroup
                        className={cn(
                            "rounded-lg border border-border-base opacity-0 group-focus-within:opacity-100 group-hover:opacity-100",
                            { "opacity-100": isPointerCoarse || forceHoverState || menuOpen },
                        )}
                        variant="ghost"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        data-tracking-id-none
                    >
                        {/* due to the complexity of CreateEnvironmentButton, we cannot use a ButtonGroupItem here, so we mimic some of the styles to match */}
                        <CreateEnvironmentButton
                            project={project}
                            size="sm"
                            variant={"ghost"}
                            className="rounded-r-none border-none focus-visible:bg-surface-accent focus-visible:ring-0 focus-visible:ring-offset-0"
                            onCreateSuccess={onCreateSuccess}
                            hideTooltip={true}
                        >
                            Create Environment
                        </CreateEnvironmentButton>
                        {shouldShowOptionsDropdown && (
                            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                                <DropdownMenuTrigger
                                    data-testid={"create-environment-from-project-dropdown-trigger-" + project.id}
                                    asChild
                                >
                                    <ButtonGroupItem
                                        data-testid="open-editor-selection-popover"
                                        LeadingIcon={IconChevronDownSmall}
                                        aria-label="Create environment with options"
                                    />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-full focus-visible:ring-0">
                                    <DropdownMenuItem asChild>
                                        <CreateEnvironmentButton
                                            project={project}
                                            variant="ghost"
                                            size="sm"
                                            className={cn(
                                                "justify-start border-none text-left font-normal focus-visible:outline-0",
                                            )}
                                            onCreateSuccess={onCreateSuccess}
                                            hideTooltip={true}
                                            showProjectEnvClassSelector={true}
                                        >
                                            Create with options
                                        </CreateEnvironmentButton>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </ButtonGroup>
                )}
            </div>
        </Link>
    );
};

type ProjectActionsProps = {
    project: PlainProject;
    editable?: boolean;
    currentMember?: PlainOrganizationMember;
    state?: ProjectState;
    creator?: PlainOrganizationMember;
};
const ProjectActions: FC<ProjectActionsProps> = ({ project, editable, currentMember, state, creator }) => {
    const navigate = useNavigate();
    const { openShareDialog } = useShareResource();
    const { value: isGroupManagementEnabled } = useFlag(Flags.GroupManagementEnabled, false);
    const isAdmin = currentMember?.role === OrganizationRole.ADMIN;
    const createPrebuild = useCreatePrebuild();
    const [isTriggeringPrebuild, setIsTriggeringPrebuild] = useState(false);

    const onCopyId = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(project.id);
            toast({
                title: "Project ID copied to clipboard",
                description: project.id,
            });
        } catch (error) {
            toast({
                title: "Failed to copy ID to your clipboard",
                description: formatError(error),
            });
        }
    }, [project.id]);

    const onCopyUrl = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(`${window.location.origin}/projects?project=${project.id}`);
            toast({
                title: "Project URL copied to clipboard",
            });
        } catch (error) {
            toast({ title: "Failed to copy URL to your clipboard", description: formatError(error) });
        }
    }, [project.id]);

    const onEdit = useCallback(() => {
        navigate(`/projects/${project.id}`);
    }, [navigate, project.id]);

    const onShare = useCallback(() => {
        openShareDialog({
            resourceType: ResourceType.PROJECT,
            resourceId: project.id,
            resourceName: project.metadata?.name || "Untitled Project",
        });
    }, [project, openShareDialog]);

    const [showDeleteProjectModal, setShowDeleteProjectModal] = useState<{
        project: PlainProject;
        state: ProjectState;
        creator?: PlainOrganizationMember;
    }>();

    const onDelete = useCallback(() => {
        if (!state) {
            return;
        }
        setShowDeleteProjectModal({ project, creator, state });
    }, [creator, project, state]);

    const onTriggerPrebuild = useCallback(async () => {
        if (!project.prebuildConfiguration?.enabled) {
            toast({
                title: "Prebuilds are not enabled",
                description: "Please enable prebuilds in project settings to trigger prebuilds",
            });
            return;
        }

        const environmentClassIds = project.prebuildConfiguration.environmentClassIds ?? [];

        if (environmentClassIds.length === 0) {
            toast({
                title: "No prebuild environment classes configured",
                description: "Please configure environment classes for this project to trigger prebuilds",
            });
            return;
        }

        try {
            setIsTriggeringPrebuild(true);
            for (const classId of environmentClassIds) {
                await createPrebuild.mutateAsync({ projectId: project.id, environmentClassId: classId });
            }
            let message = "prebuild";
            if (environmentClassIds.length > 1) {
                message += "s";
            }
            toast({ title: message + " triggered" });
        } catch (err: unknown) {
            toast({ title: "Could not trigger prebuild", description: formatError(err) });
        } finally {
            setIsTriggeringPrebuild(false);
        }
    }, [project.id, project.prebuildConfiguration, createPrebuild]);

    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <>
            <DropdownActions
                open={menuOpen}
                onOpenChange={setMenuOpen}
                triggerTestId="project-actions-dropdown-trigger"
            >
                <DropdownMenuItem onClick={onCopyId} data-tracking-id="copy-id-project-actions">
                    Copy ID
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onCopyUrl} data-tracking-id="copy-url-project-actions">
                    Copy URL
                </DropdownMenuItem>
                {project.prebuildConfiguration?.enabled && (
                    <>
                        <DropdownMenuSeparator className="bg-content-tertiary/20" />
                        <DropdownMenuItem
                            onClick={onTriggerPrebuild}
                            disabled={isTriggeringPrebuild}
                            data-testid="project-actions-dropdown-trigger-prebuild"
                            data-tracking-id="trigger-prebuild-project-actions"
                        >
                            {isTriggeringPrebuild ? "Triggering prebuild..." : "Trigger prebuild"}
                        </DropdownMenuItem>
                    </>
                )}
                {editable && (
                    <>
                        <DropdownMenuSeparator className="bg-content-tertiary/20" />
                        {isGroupManagementEnabled && isAdmin && (
                            <DropdownMenuItem
                                onClick={onShare}
                                data-testid="project-actions-dropdown-share"
                                data-tracking-id="share-project-actions"
                            >
                                Share project
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                            onClick={onEdit}
                            data-testid="project-actions-dropdown-edit"
                            data-tracking-id="edit-project-actions"
                        >
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={onDelete}
                            className="text-content-red"
                            data-tracking-id="delete-project-actions"
                        >
                            Delete
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownActions>

            {showDeleteProjectModal && (
                <DeleteProjectModal
                    project={project}
                    state={showDeleteProjectModal.state}
                    creator={showDeleteProjectModal.creator}
                    currentMember={currentMember}
                    onClose={() => setShowDeleteProjectModal(undefined)}
                />
            )}
        </>
    );
};

const PlainCard: FC<{ title: string; description: string; onClick: () => void; "data-tracking-id": string }> = ({
    title,
    description,
    onClick,
    "data-tracking-id": dataTrackingId,
}) => {
    return (
        <button
            data-testid={"project-from-url"}
            className={cn(
                "group flex h-full min-h-[200px] flex-col gap-3 rounded-lg border border-solid border-border-light bg-surface-glass p-5 text-left transition-all duration-200",
                "hover:border-border-medium cursor-pointer hover:bg-surface-primary hover:shadow-md",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-yield",
            )}
            onClick={onClick}
            data-tracking-id={dataTrackingId}
        >
            <div className="min-w-0 flex-1">
                <h3 className="mb-2 text-xl font-semibold leading-tight text-content-primary">{title}</h3>
                <div className="text-sm leading-5 text-content-secondary">{description}</div>
            </div>
            <div className="mt-auto flex items-center justify-end">
                <div className="text-xs text-content-tertiary">Click to continue</div>
            </div>
        </button>
    );
};

export const NoProjectCard: FC<{ onCreateEnvironment: () => void }> = ({ onCreateEnvironment }) => {
    return (
        <PlainCard
            title="Open Repository URL"
            description="Quickly create an environment from a repo URL"
            onClick={onCreateEnvironment}
            data-tracking-id="create-environment-from-url-no-project-card"
        />
    );
};

export const BlankEnvironmentCard: FC<{ onCreateEnvironment: () => void }> = ({ onCreateEnvironment }) => {
    return (
        <PlainCard
            title="Create Environment From Scratch"
            description="Start with a clean, empty environment"
            onClick={onCreateEnvironment}
            data-tracking-id="create-environment-from-scratch-blank-environment-card"
        />
    );
};
